import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { SubjectIcon } from "@/components/SubjectIcon";
import { remap } from "@/lib/palette";
import { MASCOT } from "@/lib/assets";
import { ArrowLeft, BookOpen, ListChecks } from "@phosphor-icons/react";

const EXAM_IMAGES = [
  "https://images.unsplash.com/photo-1536337005238-94b997371b40?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxBZnJpY2FuJTIwc3R1ZGVudHMlMjBjbGFzc3Jvb20lMjBleGFtJTIwd3JpdGluZ3xlbnwwfHx8fDE3ODk3MzYyODh8MA&ixlib=rb-4.1.0&q=85&w=1200",
  "https://images.pexels.com/photos/37456293/pexels-photo-37456293.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=1200",
  "https://images.pexels.com/photos/34162715/pexels-photo-34162715.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=1200",
  "https://images.unsplash.com/photo-1529390079861-591de354faf5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHw0fHxBZnJpY2FuJTIwc3R1ZGVudHMlMjBjbGFzc3Jvb20lMjBleGFtJTIwd3JpdGluZ3xlbnwwfHx8fDE3ODk3MzYyODh8MA&ixlib=rb-4.1.0&q=85&w=1200",
];
const EXAM_SCIENCE = "https://images.pexels.com/photos/6208710/pexels-photo-6208710.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=1200";

function pickImg(exam) {
  const s = `${exam.name} ${exam.track || ""} ${exam.classe || ""}`.toLowerCase();
  if (/scien|physi|chimi|svt|techn|industr|g[eé]nie|electr|électr|informat|\bcap\b|\bbt\b/.test(s)) return EXAM_SCIENCE;
  const key = exam.id || exam.name || "";
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h + key.charCodeAt(i)) % EXAM_IMAGES.length;
  return EXAM_IMAGES[h];
}

export default function ExamDetail() {
  const { examId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/exams/${examId}`).then((r) => setData(r.data)).catch(() => navigate("/app/cours"));
  }, [examId, navigate]);

  if (!data) return <div className="h-64 animate-pulse rounded-2xl bg-sand" />;
  const { exam, subjects } = data;

  return (
    <div className="space-y-8">
      <Link to="/app/cours" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-terracotta"><ArrowLeft /> Tous les examens</Link>

      <div className="relative overflow-hidden rounded-3xl border border-border shadow-sm animate-fade-up">
        <img src={pickImg(exam)} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0" style={{ background: `linear-gradient(100deg, ${remap(exam.color)}f2 0%, ${remap(exam.color)}d9 42%, ${remap(exam.color)}66 100%)` }} />
        <div className="relative flex items-center justify-between gap-4 p-7 sm:p-10">
          <div className="max-w-lg text-white">
            <div className="h-1.5 w-14 rounded-full bg-white/70" />
            <h1 className="mt-3 font-display text-3xl font-extrabold drop-shadow sm:text-4xl">{exam.name}</h1>
            <p className="mt-1 text-white/90">{exam.classe} · {exam.description}</p>
            <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/20 px-3 py-1 text-sm font-semibold text-white backdrop-blur">
              {subjects.length} matières disponibles
            </div>
          </div>
          <img src={MASCOT.point} alt="" className="hidden w-28 shrink-0 drop-shadow-xl sm:block lg:w-36" />
        </div>
      </div>

      <div>
        <h2 className="mb-4 font-display text-xl font-bold">Matières</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {subjects.map((s) => (
            <Link key={s.id} to={`/app/cours/${examId}/${encodeURIComponent(s.name)}`} data-testid={`subject-card-${s.id}`}
              className="group rounded-2xl border border-border bg-white p-5 transition hover:-translate-y-1 hover:shadow-[0_14px_34px_rgba(4,42,101,0.1)]">
              <div className="grid h-12 w-12 place-items-center rounded-xl transition group-hover:scale-110" style={{ background: `${remap(s.color)}1a`, color: remap(s.color) }}>
                <SubjectIcon name={s.icon} size={26} />
              </div>
              <div className="mt-4 font-display text-lg font-bold">{s.name}</div>
              <div className="mt-2 flex gap-3 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1"><BookOpen size={14} /> {s.lesson_count} leçon{s.lesson_count > 1 ? "s" : ""}</span>
                {s.has_quiz && <span className="inline-flex items-center gap-1 text-terracotta"><ListChecks size={14} /> Quiz</span>}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
